import { useState } from 'react';
import { SHOW_NOT_CAPTURED } from '@allrouted/ui/Field';

/**
 * The Setup navigation tree.
 *
 * Mirrors the incumbent grouping exactly, because dispatchers already know
 * where things live and a "better" arrangement would only cost them time.
 * Groups collapse; the one containing the current page starts open.
 */

export type SetupPage =
  | 'depots'
  | 'vehicles'
  | 'drivers'
  | 'assignment'
  | 'tags'
  | 'regions'
  | 'road-editor'
  | 'traffic'
  | 'preferences'
  | 'tracking-page'
  | 'notifications'
  | 'voice'
  | 'mobile-app'
  | 'recurrences'
  | 'webhooks'
  | 'territory';

interface Group {
  id: string;
  label: string;
  pages: { id: SetupPage; label: string; captured: boolean; hidden?: boolean }[];
}

/**
 * `captured` records whether we have actually seen the incumbent screen. The
 * ones marked false are built from first principles and flagged in the UI, so
 * nobody mistakes a plausible form for a verified one.
 */
export const SETUP_GROUPS: Group[] = [
  {
    id: 'core',
    label: '',
    pages: [
      { id: 'depots', label: 'Depots', captured: true },
      { id: 'vehicles', label: 'Vehicles', captured: true },
      { id: 'drivers', label: 'Drivers', captured: true },
      { id: 'assignment', label: 'Driver assignment', captured: true },
      { id: 'tags', label: 'Tags', captured: true },
    ],
  },
  {
    id: 'advanced',
    label: 'Advanced',
    pages: [
      { id: 'regions', label: 'Regions', captured: true },
      { id: 'road-editor', label: 'Road Editor', captured: true },
      { id: 'traffic', label: 'Traffic', captured: true },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    pages: [
      { id: 'preferences', label: 'Preferences', captured: true },
      { id: 'tracking-page', label: 'Tracking Page', captured: true },
      /* Hidden until customer notifications and voice are switched on for AllRouted. */
      { id: 'notifications', label: 'Notifications', captured: true, hidden: true },
      { id: 'voice', label: 'Voice', captured: true, hidden: true },
    ],
  },
  {
    id: 'app',
    label: 'Driver Mobile App',
    /* Hidden: drivers use their own app, so these settings would configure nothing. */
    pages: [{ id: 'mobile-app', label: 'App settings', captured: false, hidden: true }],
  },
  {
    id: 'integration',
    label: 'Integration',
    pages: [
      { id: 'recurrences', label: 'Recurring orders', captured: false },
      { id: 'webhooks', label: 'Webhooks', captured: false },
    ],
  },
  {
    id: 'territory',
    label: '',
    pages: [{ id: 'territory', label: 'Territories', captured: true }],
  },
];

/** Pages kept in the code but not offered: no menu entry, and their addresses lead to Depots. */
export const HIDDEN_SETUP_PAGES: SetupPage[] = SETUP_GROUPS.flatMap((g) => g.pages.filter((p) => p.hidden).map((p) => p.id));

/* What the menu shows: hidden pages out, and any section left empty with them. */
const VISIBLE_GROUPS: Group[] = SETUP_GROUPS.map((g) => ({ ...g, pages: g.pages.filter((p) => !p.hidden) })).filter(
  (g) => g.pages.length > 0,
);

export function SetupNav({
  page,
  onSelect,
}: {
  page: SetupPage;
  onSelect: (page: SetupPage) => void;
}) {
  const containing = VISIBLE_GROUPS.find((g) => g.pages.some((p) => p.id === page));
  /*
   * Derived rather than a hand-written list of group ids. That list had to be
   * extended for every new group, and forgetting left the group collapsed even
   * while you were standing on one of its pages -- which reads as the page
   * having vanished from the navigation.
   */
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(VISIBLE_GROUPS.map((g) => [g.id, g.id === containing?.id])),
  );

  return (
    <nav className="setup-nav" aria-label="Setup">
      <h2 className="setup-title">Territory settings</h2>

      {VISIBLE_GROUPS.map((group) => {
        if (!group.label) {
          return (
            <div key={group.id} className="setup-group">
              {group.pages.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="setup-item"
                  aria-current={page === p.id ? 'page' : undefined}
                  onClick={() => onSelect(p.id)}
                >
                  {p.label}
                  {SHOW_NOT_CAPTURED && !p.captured ? (
                    <span className="setup-flag" title="Not yet captured">
                      •
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          );
        }

        const expanded = open[group.id] ?? false;
        return (
          <div key={group.id} className="setup-group">
            <button
              type="button"
              className="setup-item setup-parent"
              aria-expanded={expanded}
              onClick={() => setOpen((o) => ({ ...o, [group.id]: !expanded }))}
            >
              {group.label}
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <path
                  d={expanded ? 'M2 3.5l3 3 3-3' : 'M3.5 2l3 3-3 3'}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {expanded
              ? group.pages.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="setup-item setup-child"
                    aria-current={page === p.id ? 'page' : undefined}
                    onClick={() => onSelect(p.id)}
                  >
                    {p.label}
                    {SHOW_NOT_CAPTURED && !p.captured ? (
                      <span className="setup-flag" title="Not yet captured">
                        •
                      </span>
                    ) : null}
                  </button>
                ))
              : null}
          </div>
        );
      })}

      {SHOW_NOT_CAPTURED ? (
        <p className="setup-legend">
          <span className="setup-flag">•</span> not yet captured from the incumbent
        </p>
      ) : null}
    </nav>
  );
}
